import { Mail, ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";
import { FadeIn, Section } from "./ui";
import { useEffect, useState, FormEvent } from "react";

function loadScriptOnce(src: string, async = false) {
  if (document.querySelector(`script[src="${src}"]`)) return;
  const script = document.createElement('script');
  script.src = src;
  script.async = async;
  document.body.appendChild(script);
}

export function NewsletterForm() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // sibforms' main.js and reCAPTCHA's api.js are only needed on this form —
  // loading them from index.html on every route made them throw on pages
  // without this component (they'd look for DOM elements that don't exist).
  useEffect(() => {
    loadScriptOnce('https://sibforms.com/forms/end-form/build/main.js', true);
    loadScriptOnce('https://www.google.com/recaptcha/api.js?hl=pt', true);
  }, []);

  useEffect(() => {
    const renderCaptcha = () => {
      const w = window as any;
      if (w.grecaptcha && w.grecaptcha.render) {
        const captchaEl = document.getElementById('sib-captcha');
        // Only render if it's empty (hasn't been rendered yet)
        if (captchaEl && captchaEl.children.length === 0) {
          try {
            w.grecaptcha.render('sib-captcha', {
              sitekey: '6LeIelEtAAAAAPRxTPPLrQenUKeAfCOXYgBv7hPH',
              callback: 'handleCaptchaResponse'
            });
          } catch (e) {
            console.error('Error rendering reCAPTCHA', e);
          }
        }
      }
    };

    // Since the external script might take a moment to load, we check or poll for it
    if ((window as any).grecaptcha) {
      renderCaptcha();
    } else {
      const interval = setInterval(() => {
        if ((window as any).grecaptcha) {
          renderCaptcha();
          clearInterval(interval);
        }
      }, 500);
      return () => clearInterval(interval);
    }
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const w = window as any;
    let recaptchaResponse = '';
    if (w.grecaptcha) {
      recaptchaResponse = w.grecaptcha.getResponse();
    }

    if (!recaptchaResponse) {
      setStatus('error');
      setErrorMessage('Por favor, confirme que você não é um robô antes de assinar.');
      return;
    }

    setStatus('loading');
    
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const actionUrl = form.action + (form.action.includes('?') ? '&' : '?') + 'isAjax=true';
      
      const response = await fetch(actionUrl, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      
      if (data.success) {
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMessage(
          data.errors && data.errors['g-recaptcha-response'] 
            ? 'A verificação de segurança falhou. Tente novamente.' 
            : 'Sua assinatura não pôde ser validada. Verifique o e-mail e tente novamente.'
        );
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus('error');
      setErrorMessage('Ocorreu um erro de conexão. Tente novamente mais tarde.');
    }
  };

  return (
    <Section className="py-24">
      <FadeIn>
        <div className="bg-brand-dark border border-brand-accent/30 rounded-[2rem] p-8 md:p-16 relative overflow-hidden shadow-2xl max-w-4xl mx-auto">
          {/* Decorative glow */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-brand-accent/20 rounded-full blur-[100px] pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-brand-accent/10 rounded-full blur-[100px] pointer-events-none" />

          <div className="relative z-10 flex flex-col md:flex-row gap-12 items-center">
            
            <div className="flex-1 text-center md:text-left">
              <div className="inline-flex items-center justify-center p-3 bg-brand-accent/10 rounded-2xl mb-6 text-brand-accent">
                <Mail className="w-8 h-8" />
              </div>
              <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter mb-4">
                Assine nossa <span className="font-highlight italic text-brand-accent lowercase font-normal">newsletter</span>
              </h2>
              <p className="text-brand-light/70 text-lg">
                Mantenha-se atualizado com os melhores insights de marketing, branding e inteligência artificial direto na sua caixa de entrada.
              </p>
            </div>

            <div className="w-full md:w-[45%] flex-shrink-0">
              <div className="sib-form" style={{ textAlign: 'left' }}>
                <div id="sib-form-container" className="sib-form-container">
                  
                  {/* Custom Messages */}
                  {status === 'error' && (
                    <div className="p-4 mb-6 text-red-800 border border-red-300 rounded-xl bg-red-50 flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                      <span className="font-medium">{errorMessage}</span>
                    </div>
                  )}
                  
                  {status === 'success' && (
                    <div className="p-4 mb-6 text-green-800 border border-green-300 rounded-xl bg-green-50 flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                      <span className="font-medium">Você fez sua assinatura com sucesso! Fique de olho na sua caixa de entrada.</span>
                    </div>
                  )}

                  {status !== 'success' && (
                    <div id="sib-container" className="sib-container--large sib-container--vertical direction-ltr">
                      <form 
                        id="sib-form" 
                        method="POST" 
                        action="https://a513534d.sibforms.com/serve/MUIFAMr2gQ1hCsUOdlzZR_Oj1gip3K3MnFMCaML4Lk_bc7myyLEj56MvPdKB17m0N4RAHAwb2DXN5qapYlf8dcdrCwYHXpKGOCWpQn01F8eAHuoH-sinHdrQtmhp7wg6xxSb0kjSme2-5Ey9Hi5lFSqyMU9_6cpuYQ9ZX41OrPleSzdW_XPAHzbkr32Z1Wc9hkXYZwr0tSGUTqlkGg==" 
                        onSubmit={handleSubmit}
                        className="flex flex-col gap-4"
                      >
                      <div className="sib-input sib-form-block">
                        <div className="form__entry entry_block">
                          <div className="form__label-row flex flex-col gap-2">
                            <label className="entry__label text-sm font-bold text-brand-light" htmlFor="EMAIL" data-required="*">
                              E-mail corporativo
                            </label>
                            <div className="entry__field relative group">
                              <input 
                                className="input w-full bg-brand-light/5 border border-brand-light/20 text-brand-light placeholder:text-brand-light/40 rounded-xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all" 
                                type="text" 
                                id="EMAIL" 
                                name="EMAIL" 
                                autoComplete="email" 
                                placeholder="seunome@empresa.com.br" 
                                data-required="true" 
                                required 
                              />
                            </div>
                          </div>
                          <label className="entry__error entry__error--primary hidden text-red-500 text-sm mt-1"></label>
                        </div>
                      </div>

                      <div className="sib-captcha sib-form-block mt-2">
                        <div className="form__entry entry_block">
                          <div className="form__label-row">
                            <div className="g-recaptcha sib-visible-recaptcha" id="sib-captcha" data-sitekey="6LeIelEtAAAAAPRxTPPLrQenUKeAfCOXYgBv7hPH" data-callback="handleCaptchaResponse" style={{direction: 'ltr'}}></div>
                          </div>
                          <label className="entry__error entry__error--primary hidden text-red-500 text-sm mt-1"></label>
                        </div>
                      </div>

                      <div className="sib-form-block mt-2">
                        <button 
                          className={`sib-form-block__button w-full bg-brand-accent hover:bg-[#C0421B] text-brand-light font-bold uppercase tracking-widest text-sm py-4 px-8 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 group cursor-pointer ${status === 'loading' ? 'opacity-80 cursor-wait' : ''}`} 
                          type="submit"
                          disabled={status === 'loading'}
                        >
                          {status === 'loading' ? (
                            <svg className="w-5 h-5 animate-spin" viewBox="0 0 512 512" fill="currentColor">
                              <path d="M460.116 373.846l-20.823-12.022c-5.541-3.199-7.54-10.159-4.663-15.874 30.137-59.886 28.343-131.652-5.386-189.946-33.641-58.394-94.896-95.833-161.827-99.676C261.028 55.961 256 50.751 256 44.352V20.309c0-6.904 5.808-12.337 12.703-11.982 83.556 4.306 160.163 50.864 202.11 123.677 42.063 72.696 44.079 162.316 6.031 236.832-3.14 6.148-10.75 8.461-16.728 5.01z" />
                            </svg>
                          ) : (
                            <>
                              Assinar Newsletter
                              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </>
                          )}
                        </button>
                      </div>

                      <input type="text" name="email_address_check" defaultValue="" className="input--hidden hidden" />
                      <input type="hidden" name="locale" defaultValue="pt" />
                    </form>
                  </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </FadeIn>
    </Section>
  );
}
